import React from "react";
import { AlertTriangle, CheckCircle2, ChevronDown } from "lucide-react";
import CitedExcelCell from "./CitedExcelCell";
import type { VerificationStatus } from "../types/citationArtifact";
import type { DerivedInputTrace } from "../utils/citationArtifact";

/**
 * Renders a derived (computed) value as a dependency-graph node: the value, a
 * Σ marker, and an expandable trace showing the formula and each input — its
 * label, value, verification, and clickable source cell. A computed-only total
 * is never shown as "verified"; the trace makes *why* legible (which inputs it
 * combined, and whether each was independently verified).
 */
export interface DerivedCellTracePresentation {
	display: string;
	verified: boolean;
	expression?: string;
	/** False for a ratio or product — see `formulaText`. Defaults to additive. */
	additive?: boolean;
	issue?: string;
	inputs: DerivedInputTrace[];
}

interface DerivedCellTraceProps {
	presentation: DerivedCellTracePresentation;
	chatId?: string | null;
}

const STATUS_TONE: Record<VerificationStatus, string> = {
	verified: "text-emerald-600",
	unverified: "text-amber-600",
	ambiguous: "text-amber-600",
	invalid: "text-red-600",
	missing: "text-text-muted",
	not_applicable: "text-text-muted",
};

const STATUS_LABEL: Record<VerificationStatus, string> = {
	verified: "Verified against source",
	unverified: "Not independently verified",
	ambiguous: "Ambiguous source match",
	invalid: "Does not match source",
	missing: "Source not found",
	not_applicable: "Not applicable",
};

/** A compact per-input verification glyph, with an accessible label. */
function StatusIcon({ status }: { status?: VerificationStatus }) {
	if (!status) return <span className="text-text-muted">—</span>;
	const title = STATUS_LABEL[status];
	if (status === "verified") {
		return (
			<CheckCircle2
				className="h-3.5 w-3.5 text-emerald-600"
				aria-label={title}
			/>
		);
	}
	if (status === "missing" || status === "not_applicable") {
		return (
			<span className="text-text-muted" title={title}>
				—
			</span>
		);
	}
	return (
		<AlertTriangle
			className={`h-3.5 w-3.5 ${STATUS_TONE[status]}`}
			aria-label={title}
		/>
	);
}

function inputName(input: DerivedInputTrace): string {
	if (input.label) return input.label;
	const c = input.citation;
	return c ? `${c.sheet}!${c.cell ?? c.range ?? ""}` : "?";
}

/** Cell reference (Sheet!Cell) for the row's secondary line, when it adds info. */
function inputRef(input: DerivedInputTrace): string | null {
	const c = input.citation;
	if (!c) return null;
	const ref = c.cell ?? c.range ?? "";
	if (!ref) return null;
	// Only worth showing when the primary name is a human label (not the ref).
	return input.label ? `${c.sheet}!${ref}` : null;
}

/**
 * Readable formula: "GPR − Vacancy Loss − Concessions + Other Income".
 *
 * Two ways to get there, and which one is used is not cosmetic. A sum or
 * difference is *reconstructed* from the per-input signs, which is what makes
 * the line read in the reader's own vocabulary. Anything else — a ratio, a
 * product — has no per-input sign that could describe it, and reconstructing one
 * anyway would print "Revenue + Total Revenue" over a number that was actually a
 * division: a false statement about the calculation, sitting under a verified
 * badge.
 *
 * So a non-additive composition shows its expression **exactly as the server
 * verified it**, references and all. Substituting each `iN` for its component's
 * label was tried and is worse: two components sharing a label render
 * "Revenue / Revenue", which loses the one thing the expression is there to
 * state — which operand is which. The references stay, and the breakdown below
 * lists the components in `i1, i2, …` order.
 */
export function formulaText(
	inputs: DerivedInputTrace[],
	expression?: string,
	additive: boolean = true,
): string {
	if (!additive && expression) {
		return expression;
	}
	return inputs
		.map((input, i) => {
			const op = input.sign < 0 ? "−" : i === 0 ? "" : "+";
			return `${op} ${inputName(input)}`.trim();
		})
		.join(" ")
		.trim();
}

const DerivedCellTrace: React.FC<DerivedCellTraceProps> = ({
	presentation,
	chatId,
}) => {
	const { display, verified, issue, inputs, expression } = presentation;
	const additive = presentation.additive ?? true;
	const hasTrace = inputs.length > 0;
	// Per-input verification only exists when each input resolved to its own
	// verified leaf; otherwise drop the column rather than show a row of dashes.
	const hasStatus = inputs.some((input) => input.status);

	return (
		<details className="group inline-block align-baseline">
			<summary className="flex cursor-pointer list-none items-center gap-1">
				<span
					className={verified ? "font-medium" : "font-medium text-amber-700"}
					aria-label={
						verified ? "Derived (verified)" : "Derived (computed, unverified)"
					}
				>
					{display}
				</span>
				{!verified && (
					<AlertTriangle
						className="h-3.5 w-3.5 text-amber-700"
						aria-label={issue ?? "Unverified computed value"}
					/>
				)}
				{hasTrace && (
					<ChevronDown className="h-3 w-3 text-text-muted transition-transform group-open:rotate-180" />
				)}
			</summary>

			{hasTrace && (
				<div className="mt-1.5 w-[24rem] max-w-[90vw] overflow-hidden rounded-lg border border-gray-200 bg-white text-left text-sm shadow-md">
					{/* Section 1 — Formula used */}
					<div className="border-b border-gray-100 bg-gray-50 px-3 py-2">
						<div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-text-muted">
							Formula used
						</div>
						<div className="whitespace-normal break-words font-mono text-xs leading-relaxed text-text-muted">
							{formulaText(inputs, expression, additive)}
							<span className="text-text-base"> = {display}</span>
						</div>
					</div>

					{/* Section 2 — Breakdown */}
					<div className="px-3 pt-2">
						<span className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
							Breakdown
						</span>
					</div>
					<table className="w-full border-collapse">
						<tbody>
							{inputs.map((input, i) => {
								const ref = inputRef(input);
								return (
									<tr
										key={i}
										className="border-b border-gray-50 last:border-0"
									>
										<td className="w-4 py-1.5 pl-3 pr-1 align-top text-center font-mono text-text-muted">
											{/* A "+" beside each component of a ratio would describe
											    an addition that did not happen. Its reference goes
											    here instead, which is what makes the verbatim
											    expression above resolvable to these rows. */}
											{additive
												? input.sign < 0
													? "−"
													: i === 0
														? ""
														: "+"
												: `i${i + 1}`}
										</td>
										<td className="max-w-[16rem] py-1.5 pr-3 align-top">
											<div className="text-text-base">{inputName(input)}</div>
											{ref && (
												<div className="font-mono text-[11px] text-text-muted">
													{ref}
												</div>
											)}
										</td>
										<td className="whitespace-nowrap py-1.5 pr-3 text-right align-top tabular-nums">
											{input.citation ? (
												<CitedExcelCell
													cell={{
														value: input.display ?? null,
														display_value: input.display ?? "",
														citations: [input.citation],
													}}
													chatId={chatId}
													className="markdown-doppler-link"
												/>
											) : (
												(input.display ?? "")
											)}
										</td>
										{hasStatus && (
											<td className="py-1.5 pr-3 text-center align-top">
												<div className="flex justify-center">
													<StatusIcon status={input.status} />
												</div>
											</td>
										)}
									</tr>
								);
							})}
						</tbody>
					</table>

					{/* Diagnostic only when something is wrong — a verified value needs
					    no restated total (it already reads at the top and in the formula). */}
					{!verified && issue && (
						<div className="flex items-start gap-1.5 border-t border-gray-100 px-3 py-2 text-xs text-amber-700">
							<AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
							<span>{issue}</span>
						</div>
					)}
				</div>
			)}
		</details>
	);
};

export default DerivedCellTrace;
